import React, { FC, useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Phase, PhaseUpdate, Task } from "@design-system";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import minMax from "dayjs/plugin/minMax";
import { Tooltip } from "antd";
import {
  CaretDownOutlined,
  LeftOutlined,
  RightOutlined,
  ExpandOutlined,
  CompressOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { TimelineModal } from "../matter-relation-editor/scope/timeline-modal";
import styles from "./matter-timeline.css";

dayjs.extend(isBetween);
dayjs.extend(minMax);

const {
  ["gantt-container"]: ganttContainerClassName,
  ["gantt-content"]: ganttContentClassName,
  ["gantt-fixed-column"]: ganttFixedColumnClassName,
  ["gantt-scroll-container"]: ganttScrollContainerClassName,
  ["gantt-scroll-button"]: ganttScrollButtonClassName,
  ["gantt-row"]: ganttRowClassName,
  ["gantt-phase"]: ganttPhaseClassName,
  ["gantt-task"]: ganttTaskClassName,
  ["gantt-title"]: ganttTitleClassName,
  ["gantt-chart"]: ganttChartClassName,
  ["gantt-bar"]: ganttBarClassName,
  ["gantt-phase-bar"]: ganttPhaseBarClassName,
  ["gantt-task-bar"]: ganttTaskBarClassName,
  ["gantt-timeline"]: ganttTimelineClassName,
  ["gantt-timeline-mark"]: ganttTimelineMarkClassName,
  ["gantt-timeline-label"]: ganttTimelineLabelClassName,
  ["gantt-today"]: ganttTodayClassName,
  ["gantt-today-label"]: ganttTodayLabelClassName,
  ["gantt-start"]: ganttStartClassName,
  ["gantt-start-label"]: ganttStartLabelClassName,
  ["gantt-deadline"]: ganttDeadlineClassName,
  ["gantt-deadline-label"]: ganttDeadlineLabelClassName,
  ["collapse-button"]: collapseButtonClassName,
  ["gantt-controls"]: ganttControlsClassName,
  ["gantt-control-button"]: ganttControlButtonClassName,
  ["gantt-column-container"]: ganttColumnContainerClassName,
  ["gantt-controls-container"]: ganttControlsContainerClassName,
  ["gantt-controls-group"]: ganttControlsGroupClassName,
  collapsed: collapsedClassName,
  left: leftClassName,
  right: rightClassName,
  ["gantt-scroll-content"]: ganttScrollContentClassName,
  ["gantt-bar-handle"]: ganttBarHandleClassName,
  ["gantt-bar-tooltip"]: ganttBarTooltipClassName,
  ["gantt-bar-clipped-start"]: ganttBarClippedStartClassName,
  ["gantt-bar-clipped-end"]: ganttBarClippedEndClassName,
  ["gantt-dependency-arrow"]: ganttDependencyArrowClassName,
  ["gantt-dependency-line"]: ganttDependencyLineClassName,
  ["gantt-dependency-arrow-head"]: ganttDependencyArrowHeadClassName,
} = styles as any;

type Props = {
  matterStartDate: Date | null;
  matterDeadlineDate: Date | null;
  phases: Phase[];
  onUpdatePhase: (phase: PhaseUpdate) => void;
  onUpdateTask: (task: Partial<Task>, phaseId: number) => void;
  viewStartDate?: Date;
  viewEndDate?: Date;
};

const MIN_PIXELS_PER_DAY = 0.1;
const MAX_PIXELS_PER_DAY = 30;
const DAY_MARK_INTERVAL = 1;
const WEEKLY_MARK_INTERVAL = 7;
const BIWEEKLY_MARK_INTERVAL = 14;
const MONTHLY_MARK_INTERVAL = 30;
const QUARTERLY_MARK_INTERVAL = 91;
const YEARLY_MARK_INTERVAL = 365;

type TimelineItemState = {
  type: "phase" | "task";
  id: number;
  phaseId?: number;
};

type TimelineEditState = TimelineItemState & {
  title: string;
  startDate?: Date | null;
  deadlineDate?: Date | null;
};

type DragState = TimelineItemState & {
  handle: "left" | "right" | "move" | "create";
  initialX: number;
  initialStartDate: Date;
  initialEndDate: Date;
};

type MouseDownState = TimelineItemState & {
  initialX: number;
  startDate: Date;
  endDate: Date;
};

export const GanttChart: FC<Props> = ({
  matterStartDate,
  matterDeadlineDate,
  phases,
  onUpdatePhase,
  onUpdateTask,
  viewStartDate,
  viewEndDate,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fixedColumnRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [pixelsPerDay, setPixelsPerDay] = useState(10);

  // Calculate the effective start and end dates for the timeline view
  const { startDate, endDate } = useMemo(() => {
    // If view dates are provided, use them exactly as they are
    if (viewStartDate && viewEndDate) {
      return {
        startDate: viewStartDate,
        endDate: viewEndDate,
      };
    }

    // Otherwise, calculate based on matter dates and phase/task dates
    let minDate: Date | null = matterStartDate;
    let maxDate: Date | null = matterDeadlineDate;

    // Check all phases and tasks, updating min/max dates if they extend beyond matter dates
    phases.forEach((phase) => {
      if (phase.startDate && (!minDate || phase.startDate < minDate)) {
        minDate = phase.startDate;
      }
      if (phase.deadlineDate && (!maxDate || phase.deadlineDate > maxDate)) {
        maxDate = phase.deadlineDate;
      }

      phase.tasks?.forEach((task) => {
        if (task.startDate && (!minDate || task.startDate < minDate)) {
          minDate = task.startDate;
        }
        if (task.deadlineDate && (!maxDate || task.deadlineDate > maxDate)) {
          maxDate = task.deadlineDate;
        }
      });
    });

    // If still no dates are set, use today and 3 months from now
    if (!minDate || !maxDate) {
      minDate = new Date();
      maxDate = dayjs().add(3, "month").toDate();
    }

    // Add padding to the timeline only for the fallback case
    return {
      startDate: dayjs(minDate).subtract(7, "day").toDate(),
      endDate: dayjs(maxDate).add(1, "month").toDate(),
    };
  }, [phases, matterStartDate, matterDeadlineDate, viewStartDate, viewEndDate]);

  // Adjust zoom level based on the actual start and end dates used for rendering
  useEffect(() => {
    if (startDate && endDate && scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      // Ensure daysInView is at least 1 to avoid division by zero
      const daysInView = Math.max(1, dayjs(endDate).diff(startDate, "day"));

      // Calculate pixels per day to fill container width
      // Subtract a small amount to account for padding/margins
      const optimalPixelsPerDay = (containerWidth - 40) / daysInView;

      // Determine appropriate minimum pixels per day based on timeline length
      let dynamicMinPixelsPerDay = MIN_PIXELS_PER_DAY;

      // For shorter timelines (less than 6 months), use a higher minimum
      if (daysInView < 180) {
        dynamicMinPixelsPerDay = 1;
      } else if (daysInView < 365) {
        // For timelines between 6-12 months
        dynamicMinPixelsPerDay = 0.5;
      } else if (daysInView < 730) {
        // For timelines between 1-2 years
        dynamicMinPixelsPerDay = 0.25;
      }
      // For very long timelines (2+ years), use the base minimum (0.1)

      // Clamp the value between dynamic minimum and MAX
      const clampedPixelsPerDay = Math.max(
        dynamicMinPixelsPerDay,
        Math.min(MAX_PIXELS_PER_DAY, optimalPixelsPerDay)
      );

      setPixelsPerDay(clampedPixelsPerDay);
    }
  }, [startDate, endDate]);

  const [timelineEdit, setTimelineEdit] = useState<TimelineEditState | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [mouseDownState, setMouseDownState] = useState<MouseDownState | null>(null);

  // Filter phases and tasks based on view range
  const filteredPhases = useMemo(() => {
    if (!viewStartDate || !viewEndDate) return phases;

    return phases
      .map((phase) => {
        // Include phase if it overlaps with view range
        const phaseOverlaps =
          phase.startDate &&
          phase.deadlineDate &&
          dayjs(phase.deadlineDate).isAfter(viewStartDate) &&
          dayjs(phase.startDate).isBefore(viewEndDate);

        if (!phaseOverlaps) return null;

        // Filter tasks within the phase
        const filteredTasks =
          phase.tasks?.filter(
            (task) =>
              task.startDate &&
              task.deadlineDate &&
              dayjs(task.deadlineDate).isAfter(viewStartDate) &&
              dayjs(task.startDate).isBefore(viewEndDate)
          ) || null;

        return {
          ...phase,
          tasks: filteredTasks,
        };
      })
      .filter(Boolean) as Phase[];
  }, [phases, viewStartDate, viewEndDate]);

  const [collapsedPhases, setCollapsedPhases] = useState<{ [key: number]: boolean }>(() =>
    phases.reduce((acc, phase) => ({ ...acc, [phase.id]: true }), {})
  );

  const [localPhases, setLocalPhases] = useState(filteredPhases);
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Update local phases when props or filtered phases change
  useEffect(() => {
    if (!dragState && !pendingUpdate) {
      const phasesAreDifferent = JSON.stringify(filteredPhases) !== JSON.stringify(localPhases);
      if (phasesAreDifferent) {
        setLocalPhases(filteredPhases);
      }
    }
  }, [filteredPhases, dragState, pendingUpdate, localPhases]);

  const sortedPhases = useMemo(() => {
    return [...localPhases]
      .sort((a, b) => a.order - b.order)
      .map((phase) => ({
        ...phase,
        tasks: phase.tasks?.slice().sort((a, b) => a.order - b.order) || null,
      }));
  }, [localPhases]);

  // Calculate total days based on the view range if provided, otherwise use the full range
  const totalDays = dayjs(endDate).diff(startDate, "day");
  const timelineWidth = totalDays * pixelsPerDay;

  const today = new Date();

  // Add constants for label widths (in pixels)
  const TODAY_LABEL_WIDTH = 45; // "Today" text width + padding
  const START_LABEL_WIDTH = 45; // "Start" text width + padding
  const DEADLINE_LABEL_WIDTH = 70; // "Deadline" text width + padding

  // Calculate positions relative to the current view's start date
  const todayPosition = dayjs(today).isBetween(startDate, endDate, "day", "[]")
    ? dayjs(today).diff(startDate, "day") * pixelsPerDay
    : -1;

  const startPosition =
    matterStartDate && dayjs(matterStartDate).isBetween(startDate, endDate, "day", "[]")
      ? dayjs(matterStartDate).diff(startDate, "day") * pixelsPerDay
      : null;

  const deadlinePosition =
    matterDeadlineDate && dayjs(matterDeadlineDate).isBetween(startDate, endDate, "day", "[]")
      ? dayjs(matterDeadlineDate).diff(startDate, "day") * pixelsPerDay
      : null;

  // Only show markers if they fall within the current view
  const showToday = todayPosition >= 0 && todayPosition <= timelineWidth;

  // Determine if labels should be shown based on proximity and visibility
  const showTodayLabel =
    showToday &&
    (!startPosition || Math.abs(todayPosition - (startPosition || 0)) > TODAY_LABEL_WIDTH);
  const showStartLabel =
    startPosition !== null &&
    (!deadlinePosition || Math.abs((deadlinePosition || 0) - startPosition) > START_LABEL_WIDTH);

  const getPositionAndWidth = (start: Date | null | undefined, end: Date | null | undefined) => {
    if (!start || !end) return { left: 0, width: 0, isClippedStart: false, isClippedEnd: false };

    // If we have view dates, clip the start and end dates to the view range
    if (viewStartDate && viewEndDate) {
      const isClippedStart = dayjs(start).isBefore(viewStartDate);
      const isClippedEnd = dayjs(end).isAfter(viewEndDate);

      const clippedStart = dayjs.max(dayjs(start), dayjs(viewStartDate));
      const clippedEnd = dayjs.min(dayjs(end), dayjs(viewEndDate));

      // Use the component's main startDate for calculating the diff
      const startDiff = clippedStart.diff(startDate, "day");
      const duration = clippedEnd.diff(clippedStart, "day");

      return {
        left: startDiff * pixelsPerDay,
        width: duration * pixelsPerDay,
        isClippedStart,
        isClippedEnd,
      };
    }

    // If no view dates, show full bars relative to the component's main startDate
    const startDiff = dayjs(start).diff(startDate, "day");
    const duration = dayjs(end).diff(start, "day");

    return {
      left: startDiff * pixelsPerDay,
      width: duration * pixelsPerDay,
      isClippedStart: false,
      isClippedEnd: false,
    };
  };

  const renderTimelineMarks = () => {
    const marks = [];

    // Determine the appropriate interval based on zoom level
    let interval = WEEKLY_MARK_INTERVAL;
    let dateFormat = "MMM D"; // Default format: "Jan 15"

    // Adjust intervals based on zoom level to prevent label overlap
    if (pixelsPerDay >= 15) {
      // At high zoom levels, show every 3rd day but still include month info
      interval = DAY_MARK_INTERVAL * 3;
      dateFormat = "MMM D"; // Keep month + day: "Jan 15"
    } else if (pixelsPerDay >= 8) {
      // Medium-high zoom, show weekly marks
      interval = WEEKLY_MARK_INTERVAL;
      dateFormat = "MMM D"; // Month + day: "Jan 15"
    } else if (pixelsPerDay >= 3) {
      // Medium zoom, show biweekly marks
      interval = BIWEEKLY_MARK_INTERVAL;
      dateFormat = "MMM D"; // Month + day: "Jan 15"
    } else if (pixelsPerDay >= 1.8) {
      // Medium-low zoom, show monthly marks
      interval = MONTHLY_MARK_INTERVAL;
      dateFormat = "MMM YYYY"; // Month + year: "Jan 2023"
    } else if (pixelsPerDay >= 1) {
      // Lower medium-low zoom, show every other month
      interval = MONTHLY_MARK_INTERVAL * 2; // Every 2 months
      dateFormat = "MMM YYYY"; // Month + year: "Jan 2023"
    } else if (pixelsPerDay >= 0.5) {
      // Low zoom, show quarterly marks
      interval = QUARTERLY_MARK_INTERVAL;
      dateFormat = "Q[Q] YYYY"; // Quarter + year: "Q1 2023"
    } else if (pixelsPerDay >= 0.2) {
      // Very low zoom, show every 6 months
      interval = MONTHLY_MARK_INTERVAL * 6; // Every 6 months
      dateFormat = "MMM YYYY"; // Month + year: "Jan 2023"
    } else {
      // Extremely low zoom, show yearly marks
      interval = YEARLY_MARK_INTERVAL;
      dateFormat = "YYYY"; // Just the year: "2023"
    }

    // Calculate how many marks to render
    const numMarks = Math.floor(totalDays / interval);

    // Calculate the starting date aligned to the interval
    let firstMarkDate;

    // Handle special case for high zoom level
    if (pixelsPerDay >= 15) {
      // At high zoom, align to the nearest multiple of 3 days starting from month beginning
      const monthStart = dayjs(startDate).startOf("month");
      const dayOfMonth = dayjs(startDate).date();
      const remainder = dayOfMonth % 3;
      const daysToAdd = remainder === 0 ? 0 : 3 - remainder;
      firstMarkDate = monthStart.add(daysToAdd, "day");

      // If first mark is before our start date, move to next mark
      if (firstMarkDate.isBefore(startDate)) {
        firstMarkDate = firstMarkDate.add(3, "day");
      }
    } else if (
      interval === MONTHLY_MARK_INTERVAL ||
      interval === MONTHLY_MARK_INTERVAL * 2 ||
      interval === MONTHLY_MARK_INTERVAL * 6
    ) {
      // Start at beginning of the month
      firstMarkDate = dayjs(startDate).startOf("month");
    } else if (interval === QUARTERLY_MARK_INTERVAL) {
      // Start at beginning of the quarter
      firstMarkDate = dayjs(startDate).startOf("quarter");
    } else if (interval === YEARLY_MARK_INTERVAL) {
      // Start at beginning of the year
      firstMarkDate = dayjs(startDate).startOf("year");
    } else if (interval === WEEKLY_MARK_INTERVAL) {
      // For weekly marks, start at beginning of the week
      firstMarkDate = dayjs(startDate).startOf("week");
    } else if (interval === BIWEEKLY_MARK_INTERVAL) {
      // For biweekly marks, start at beginning of the month, then align to even weeks
      const monthStart = dayjs(startDate).startOf("month");
      const weekOfMonth = Math.ceil(dayjs(startDate).diff(monthStart, "day") / 7);
      const remainder = weekOfMonth % 2;
      const weeksToAdd = remainder === 0 ? 0 : 1;
      firstMarkDate = monthStart.add(weeksToAdd * 7, "day");

      // If first mark is before our start date, move to next mark
      if (firstMarkDate.isBefore(startDate)) {
        firstMarkDate = firstMarkDate.add(14, "day");
      }
    } else {
      // For other intervals, just use the start date
      firstMarkDate = dayjs(startDate);
    }

    // Estimate label width based on format
    const getEstimatedLabelWidth = (format: string) => {
      switch (format) {
        case "D":
          return 10; // Just day number (1-31)
        case "MMM D":
          return 45; // "Jan 15"
        case "MMM YYYY":
          return 65; // "Jan 2023"
        case "Q[Q] YYYY":
          return 55; // "Q1 2023"
        case "YYYY":
          return 35; // "2023"
        default:
          return 50;
      }
    };

    const estimatedLabelWidth = getEstimatedLabelWidth(dateFormat);
    const minSpaceBetweenLabels = estimatedLabelWidth + 5; // Add 5px padding
    const minPixelsBetweenMarks = minSpaceBetweenLabels / pixelsPerDay;

    // If our interval is too small to prevent label overlap, adjust it
    let adjustedInterval = interval;
    if (interval * pixelsPerDay < minSpaceBetweenLabels) {
      adjustedInterval = Math.ceil(minPixelsBetweenMarks / interval) * interval;
    }

    // For high zoom levels, check if we need more space between marks
    if (pixelsPerDay >= 15 && adjustedInterval === DAY_MARK_INTERVAL * 3) {
      // If "MMM D" format is too wide for every 3rd day, use every 5th day
      if (3 * pixelsPerDay < minSpaceBetweenLabels) {
        adjustedInterval = DAY_MARK_INTERVAL * 5;
      }
    }

    // Add special month separators for high zoom levels
    const monthSeparators: React.ReactNode[] = [];
    if (pixelsPerDay >= 8) {
      let currentDate = dayjs(startDate);
      const endDateDayjs = dayjs(endDate);

      while (currentDate.isBefore(endDateDayjs)) {
        // Move to first day of next month
        const nextMonth = currentDate.add(1, "month").startOf("month");

        // If next month is within range, add a separator
        if (nextMonth.isBefore(endDateDayjs)) {
          const left = nextMonth.diff(startDate, "day") * pixelsPerDay;
          monthSeparators.push(
            <div
              key={`month-${nextMonth.format("YYYY-MM")}`}
              style={{
                position: "absolute",
                left,
                bottom: 0,
                top: 0,
                width: 1,
                background: "var(--border-color)",
                opacity: 0.5,
              }}
            />
          );
        }

        currentDate = nextMonth;
      }
    }

    // Generate marks with the adjusted interval
    for (let i = 0; i <= numMarks + 1; i++) {
      const date = firstMarkDate.add(i * adjustedInterval, "day");

      // Skip if the date is outside our range
      if (date.isBefore(startDate) || date.isAfter(endDate)) continue;

      // Calculate position
      const left = date.diff(startDate, "day") * pixelsPerDay;

      // Add special formatting for first day of month when using high zoom
      let dateLabel = date.format(dateFormat);
      let isMonthStart = false;

      if (pixelsPerDay >= 15 && date.date() === 1) {
        isMonthStart = true;
        dateLabel = date.format("MMM D"); // Ensure month is always shown for 1st of month
      }

      marks.push(
        <div key={i}>
          <div
            className={ganttTimelineMarkClassName}
            style={{
              left,
              height: isMonthStart ? 12 : 8, // Make month start marks taller
            }}
          />
          <div
            className={ganttTimelineLabelClassName}
            style={{
              left,
              width: estimatedLabelWidth,
              textAlign: "center",
              fontWeight: isMonthStart ? "bold" : "normal", // Bold for month start
            }}
          >
            {dateLabel}
          </div>
        </div>
      );
    }

    return (
      <>
        {monthSeparators}
        {marks}
      </>
    );
  };

  const togglePhase = (phaseId: number, e: React.MouseEvent) => {
    // Don't toggle if clicking on the bar or handles
    if (
      (e.target as HTMLElement).closest(`.${ganttBarClassName}`) ||
      (e.target as HTMLElement).closest(`.${ganttBarHandleClassName}`)
    ) {
      return;
    }
    setCollapsedPhases((prev) => ({
      ...prev,
      [phaseId]: !prev[phaseId],
    }));
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, scrollWidth, clientWidth, scrollTop } = e.currentTarget;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth);

    // Synchronize vertical scroll of fixed column with chart area
    if (fixedColumnRef.current) {
      fixedColumnRef.current.scrollTop = scrollTop;
    }
  };

  // Add handler for fixed column scroll
  const handleFixedColumnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;

    // Synchronize vertical scroll of chart area with fixed column
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollTop;
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const { clientWidth } = scrollContainerRef.current;
      const scrollAmount = clientWidth * 0.5; // Scroll by half the visible width
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const [hoveredRow, setHoveredRow] = useState<{ type: "phase" | "task"; id: number } | null>(null);

  const expandAll = () => {
    setCollapsedPhases(phases.reduce((acc, phase) => ({ ...acc, [phase.id]: false }), {}));
  };

  const collapseAll = () => {
    setCollapsedPhases(phases.reduce((acc, phase) => ({ ...acc, [phase.id]: true }), {}));
  };

  const handleTimelineChange = (values: {
    startDate?: Date | null;
    deadlineDate?: Date | null;
  }) => {
    if (!timelineEdit) return;

    // Set pending update flag before making the server update
    setPendingUpdate(true);

    // Update local state immediately
    setLocalPhases((currentPhases: Phase[]) =>
      currentPhases.map((phase: Phase): Phase => {
        if (timelineEdit.type === "phase" && phase.id === timelineEdit.id) {
          return {
            ...phase,
            startDate: values.startDate,
            deadlineDate: values.deadlineDate,
          };
        } else if (timelineEdit.type === "task" && phase.id === timelineEdit.phaseId) {
          return {
            ...phase,
            tasks:
              phase.tasks?.map((task) =>
                task.id === timelineEdit.id
                  ? {
                      ...task,
                      startDate: values.startDate,
                      deadlineDate: values.deadlineDate,
                    }
                  : task
              ) || null,
          };
        }
        return phase;
      })
    );

    // Make the server update
    if (timelineEdit.type === "phase") {
      onUpdatePhase({
        id: timelineEdit.id,
        startDate: values.startDate,
        deadlineDate: values.deadlineDate,
      });
      // Reset pending update after a short delay to allow for optimistic updates
      setTimeout(() => setPendingUpdate(false), 100);
    } else {
      onUpdateTask(
        {
          id: timelineEdit.id,
          startDate: values.startDate,
          deadlineDate: values.deadlineDate,
        },
        timelineEdit.phaseId!
      );
      // Reset pending update after a short delay to allow for optimistic updates
      setTimeout(() => setPendingUpdate(false), 100);
    }
    setTimelineEdit(null);
  };

  const handleZoomIn = () => {
    setPixelsPerDay((prev) => {
      // Use different scaling factors based on current zoom level
      let scaleFactor = 1.2; // Default scale factor

      if (prev < 1) {
        // When very zoomed out, use larger steps
        scaleFactor = 1.5;
      } else if (prev > 15) {
        // When very zoomed in, use smaller steps
        scaleFactor = 1.1;
      }

      const newZoom = Math.min(prev * scaleFactor, MAX_PIXELS_PER_DAY);
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setPixelsPerDay((prev) => {
      // Use different scaling factors based on current zoom level
      let scaleFactor = 1.2; // Default scale factor

      if (prev < 1) {
        // When already zoomed out, use smaller steps
        scaleFactor = 1.1;
      } else if (prev > 15) {
        // When very zoomed in, use larger steps
        scaleFactor = 1.5;
      }

      // Calculate dynamic min pixels per day based on timeline length
      const daysInView = Math.max(1, dayjs(endDate).diff(startDate, "day"));
      let dynamicMinPixelsPerDay = MIN_PIXELS_PER_DAY;

      if (daysInView < 180) {
        dynamicMinPixelsPerDay = 1;
      } else if (daysInView < 365) {
        dynamicMinPixelsPerDay = 0.5;
      } else if (daysInView < 730) {
        dynamicMinPixelsPerDay = 0.25;
      }

      const newZoom = Math.max(prev / scaleFactor, dynamicMinPixelsPerDay);
      return newZoom;
    });
  };

  const handleDrag = useCallback(
    (e: MouseEvent) => {
      if (!dragState) return;

      const deltaX = e.clientX - dragState.initialX;
      const deltaDays = Math.round(deltaX / pixelsPerDay);

      if (dragState.handle === "create") {
        // For creating new bars, we want to allow dragging in either direction
        const newStartDate = dragState.initialStartDate;
        const newEndDate = dayjs(dragState.initialStartDate).add(deltaDays, "day").toDate();

        // Ensure start date is always before end date
        const [finalStartDate, finalEndDate] =
          deltaDays >= 0 ? [newStartDate, newEndDate] : [newEndDate, newStartDate];

        setLocalPhases((currentPhases) => {
          return currentPhases.map((phase): Phase => {
            if (dragState.type === "phase" && phase.id === dragState.id) {
              return {
                ...phase,
                startDate: finalStartDate,
                deadlineDate: finalEndDate,
              };
            } else if (dragState.type === "task" && phase.id === dragState.phaseId) {
              return {
                ...phase,
                tasks:
                  phase.tasks?.map((task) =>
                    task.id === dragState.id
                      ? {
                          ...task,
                          startDate: finalStartDate,
                          deadlineDate: finalEndDate,
                        }
                      : task
                  ) || null,
              };
            }
            return phase;
          });
        });
        return;
      }

      setLocalPhases((currentPhases) => {
        return currentPhases.map((phase): Phase => {
          if (dragState.type === "phase" && phase.id === dragState.id) {
            if (dragState.handle === "move") {
              const newStartDate = dayjs(dragState.initialStartDate).add(deltaDays, "day").toDate();
              const newEndDate = dayjs(dragState.initialEndDate).add(deltaDays, "day").toDate();
              return {
                ...phase,
                startDate: newStartDate,
                deadlineDate: newEndDate,
              };
            } else {
              return {
                ...phase,
                startDate:
                  dragState.handle === "left"
                    ? dayjs(dragState.initialStartDate).add(deltaDays, "day").toDate()
                    : phase.startDate,
                deadlineDate:
                  dragState.handle === "right"
                    ? dayjs(dragState.initialEndDate).add(deltaDays, "day").toDate()
                    : phase.deadlineDate,
              };
            }
          } else if (dragState.type === "task" && phase.id === dragState.phaseId) {
            return {
              ...phase,
              tasks:
                phase.tasks?.map((task) =>
                  task.id === dragState.id
                    ? {
                        ...task,
                        startDate:
                          dragState.handle === "move"
                            ? dayjs(dragState.initialStartDate).add(deltaDays, "day").toDate()
                            : dragState.handle === "left"
                            ? dayjs(dragState.initialStartDate).add(deltaDays, "day").toDate()
                            : task.startDate,
                        deadlineDate:
                          dragState.handle === "move"
                            ? dayjs(dragState.initialEndDate).add(deltaDays, "day").toDate()
                            : dragState.handle === "right"
                            ? dayjs(dragState.initialEndDate).add(deltaDays, "day").toDate()
                            : task.deadlineDate,
                      }
                    : task
                ) || null,
            };
          }
          return phase;
        });
      });
    },
    [dragState, pixelsPerDay]
  );

  const handleDragEnd = useCallback(() => {
    if (!dragState) return;

    // Find the final state and send the update
    const phase = localPhases.find((p) =>
      dragState.type === "phase" ? p.id === dragState.id : p.id === dragState.phaseId
    );

    if (!phase) return;

    // Set pending update flag before making the server update
    setPendingUpdate(true);

    if (dragState.type === "phase") {
      onUpdatePhase({
        id: dragState.id,
        startDate: phase.startDate,
        deadlineDate: phase.deadlineDate,
      });
      // Reset pending update after a short delay to allow for optimistic updates
      setTimeout(() => setPendingUpdate(false), 100);
    } else {
      const task = phase.tasks?.find((t) => t.id === dragState.id);
      if (!task) return;

      onUpdateTask(
        {
          id: dragState.id,
          startDate: task.startDate,
          deadlineDate: task.deadlineDate,
        },
        dragState.phaseId!
      );
      // Reset pending update after a short delay to allow for optimistic updates
      setTimeout(() => setPendingUpdate(false), 100);
    }

    setDragState(null);
    // Reset isDragging after a short delay to prevent the click handler from firing
    setTimeout(() => {
      setIsDragging(false);
    }, 0);
  }, [dragState, localPhases, onUpdatePhase, onUpdateTask]);

  // Add event listeners for drag
  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("mouseup", handleDragEnd);
      return () => {
        window.removeEventListener("mousemove", handleDrag);
        window.removeEventListener("mouseup", handleDragEnd);
      };
    }
  }, [dragState, handleDrag, handleDragEnd]);

  const handleDragStart = useCallback(
    (
      e: React.MouseEvent,
      type: "phase" | "task",
      id: number,
      handle: "left" | "right" | "move",
      startDate: Date | null | undefined,
      endDate: Date | null | undefined,
      phaseId?: number
    ) => {
      e.stopPropagation();
      if (!startDate || !endDate) return;

      setIsDragging(true);
      setDragState({
        type,
        id,
        handle,
        initialX: e.clientX,
        initialStartDate: startDate,
        initialEndDate: endDate,
        phaseId,
      });
    },
    []
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (mouseDownState) {
        const deltaX = Math.abs(e.clientX - mouseDownState.initialX);
        // Only start dragging if mouse has moved more than 3 pixels
        if (deltaX > 3) {
          setIsDragging(true);
          setDragState({
            type: mouseDownState.type,
            id: mouseDownState.id,
            handle: "move",
            initialX: mouseDownState.initialX,
            initialStartDate: mouseDownState.startDate,
            initialEndDate: mouseDownState.endDate,
            phaseId: mouseDownState.phaseId,
          });
          setMouseDownState(null);
        }
      }
    },
    [mouseDownState]
  );

  const handleMouseUp = useCallback(() => {
    if (mouseDownState) {
      // If we have mouseDownState but haven't started dragging, this was a click
      setMouseDownState(null);
    }
  }, [mouseDownState]);

  // Add event listeners for mouse move/up when mouseDownState exists
  useEffect(() => {
    if (mouseDownState) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [mouseDownState, handleMouseMove, handleMouseUp]);

  const renderBar = (type: "phase" | "task", item: Phase | Task, phaseId?: number) => {
    if (!item.startDate || !item.deadlineDate) return null;

    const { left, width, isClippedStart, isClippedEnd } = getPositionAndWidth(
      item.startDate,
      item.deadlineDate
    );

    const tooltipText = `${dayjs(item.startDate).format("MMM D")} - ${dayjs(
      item.deadlineDate
    ).format("MMM D")}`;

    return (
      <div
        className={`${ganttBarClassName} ${
          type === "phase" ? ganttPhaseBarClassName : ganttTaskBarClassName
        } ${isClippedStart ? ganttBarClippedStartClassName : ""} ${
          isClippedEnd ? ganttBarClippedEndClassName : ""
        }`}
        style={{ left, width, cursor: isDragging ? "grabbing" : "grab" }}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDragging) {
            setTimelineEdit({
              type,
              id: item.id,
              title: item.title,
              startDate: item.startDate,
              deadlineDate: item.deadlineDate,
              phaseId,
            });
          }
        }}
        onMouseDown={(e) => {
          // Only handle drag if clicking the bar itself, not the handles
          if (!(e.target as HTMLElement).closest(`.${ganttBarHandleClassName}`)) {
            e.stopPropagation();
            setMouseDownState({
              type,
              id: item.id,
              initialX: e.clientX,
              startDate: item.startDate as Date,
              endDate: item.deadlineDate as Date,
              phaseId,
            });
          }
        }}
      >
        <div className={ganttBarTooltipClassName}>{tooltipText}</div>
        <div
          className={`${ganttBarHandleClassName} ${leftClassName}`}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleDragStart(e, type, item.id, "left", item.startDate, item.deadlineDate, phaseId);
          }}
        />
        <div
          className={`${ganttBarHandleClassName} ${rightClassName}`}
          onMouseDown={(e) => {
            e.stopPropagation();
            handleDragStart(e, type, item.id, "right", item.startDate, item.deadlineDate, phaseId);
          }}
        />
      </div>
    );
  };

  const handleChartAreaMouseDown = (
    e: React.MouseEvent,
    type: "phase" | "task",
    id: number,
    phaseId?: number
  ) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const days = Math.floor(x / pixelsPerDay);
    // Use the main startDate for calculating click date
    const clickDate = dayjs(startDate).add(days, "day").toDate();

    setDragState({
      type,
      id,
      handle: "create",
      initialX: e.clientX,
      initialStartDate: clickDate,
      initialEndDate: clickDate,
      phaseId,
    });
  };

  return (
    <div
      className={ganttContainerClassName}
      style={{ cursor: dragState ? "ew-resize" : "default" }}
    >
      {phases.length === 0 ? (
        <div
          style={{
            padding: "var(--padding-lg)",
            textAlign: "center",
            color: "var(--text-color-secondary)",
            background: "var(--header-bg-color)",
            borderRadius: "var(--border-radius-base)",
          }}
        >
          Add phases to the scope to edit the proposal timeline
        </div>
      ) : (
        <>
          <button
            className={`${ganttScrollButtonClassName} ${leftClassName}`}
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
          >
            <LeftOutlined />
          </button>
          <button
            className={`${ganttScrollButtonClassName} ${rightClassName}`}
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
          >
            <RightOutlined />
          </button>
          <div className={ganttContentClassName}>
            {/* Fixed left column with two parts: controls and scrollable content */}
            <div className={ganttColumnContainerClassName}>
              {/* Fixed controls that never scroll */}
              <div className={ganttControlsContainerClassName}>
                <div className={ganttControlsGroupClassName}>
                  <Tooltip title="Collapse All" placement="bottom">
                    <div className={ganttControlButtonClassName} onClick={collapseAll}>
                      <CompressOutlined />
                    </div>
                  </Tooltip>
                  <Tooltip title="Expand All" placement="bottom">
                    <div className={ganttControlButtonClassName} onClick={expandAll}>
                      <ExpandOutlined />
                    </div>
                  </Tooltip>
                </div>
                <div className={ganttControlsGroupClassName}>
                  <Tooltip title="Zoom In" placement="bottom">
                    <div
                      className={ganttControlButtonClassName}
                      onClick={handleZoomIn}
                      style={{ opacity: pixelsPerDay >= MAX_PIXELS_PER_DAY ? 0.5 : 1 }}
                    >
                      <ZoomInOutlined />
                    </div>
                  </Tooltip>
                  <Tooltip title="Zoom Out" placement="bottom">
                    <div
                      className={ganttControlButtonClassName}
                      onClick={handleZoomOut}
                      style={{ opacity: pixelsPerDay <= MIN_PIXELS_PER_DAY ? 0.5 : 1 }}
                    >
                      <ZoomOutOutlined />
                    </div>
                  </Tooltip>
                </div>
              </div>

              {/* Scrollable part of the fixed column */}
              <div
                className={ganttFixedColumnClassName}
                ref={fixedColumnRef}
                onScroll={handleFixedColumnScroll}
              >
                {sortedPhases.map((phase) => (
                  <React.Fragment key={phase.id}>
                    <div
                      className={`${ganttRowClassName} ${ganttPhaseClassName}`}
                      onClick={(e) => togglePhase(phase.id, e)}
                      onMouseEnter={() => setHoveredRow({ type: "phase", id: phase.id })}
                      onMouseLeave={() => setHoveredRow(null)}
                      data-hovered={hoveredRow?.type === "phase" && hoveredRow.id === phase.id}
                    >
                      <div className={ganttTitleClassName}>
                        <CaretDownOutlined
                          className={`${collapseButtonClassName} ${
                            collapsedPhases[phase.id] ? collapsedClassName : ""
                          }`}
                        />
                        <Tooltip title={phase.title} placement="right" mouseEnterDelay={0.2}>
                          <span>{phase.title}</span>
                        </Tooltip>
                      </div>
                    </div>

                    {phase.tasks?.map((task) => (
                      <div
                        key={task.id}
                        className={`${ganttRowClassName} ${ganttTaskClassName} ${
                          collapsedPhases[phase.id] ? collapsedClassName : ""
                        }`}
                        onMouseEnter={() => setHoveredRow({ type: "task", id: task.id })}
                        onMouseLeave={() => setHoveredRow(null)}
                        data-hovered={hoveredRow?.type === "task" && hoveredRow.id === task.id}
                      >
                        <div className={ganttTitleClassName}>
                          <Tooltip title={task.title} placement="right" mouseEnterDelay={0.2}>
                            <span>{task.title}</span>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Scrollable chart area */}
            <div
              className={ganttScrollContainerClassName}
              ref={scrollContainerRef}
              onScroll={handleScroll}
            >
              <div className={ganttScrollContentClassName} style={{ width: timelineWidth }}>
                <div className={ganttTimelineClassName}>
                  {renderTimelineMarks()}
                  {showToday && (
                    <>
                      <div
                        className={ganttTodayClassName}
                        style={{
                          left: todayPosition,
                          height: "35px",
                          top: "13px",
                        }}
                      />
                      {showTodayLabel && (
                        <div className={ganttTodayLabelClassName} style={{ left: todayPosition }}>
                          Today
                        </div>
                      )}
                    </>
                  )}
                  {matterStartDate && startPosition !== null && (
                    <>
                      <div
                        className={ganttStartClassName}
                        style={{
                          left: startPosition,
                          height: "35px",
                          top: "13px",
                        }}
                      />
                      {showStartLabel && (
                        <div
                          className={ganttStartLabelClassName}
                          style={{
                            left: startPosition,
                          }}
                        >
                          Start
                        </div>
                      )}
                    </>
                  )}
                  {matterDeadlineDate && deadlinePosition !== null && (
                    <>
                      <div
                        className={ganttDeadlineClassName}
                        style={{
                          left: deadlinePosition,
                          height: "35px",
                          top: "13px",
                        }}
                      />
                      <div
                        className={ganttDeadlineLabelClassName}
                        style={{
                          left: deadlinePosition,
                        }}
                      >
                        Deadline
                      </div>
                    </>
                  )}
                </div>

                {sortedPhases.map((phase) => (
                  <React.Fragment key={phase.id}>
                    <div
                      className={`${ganttRowClassName} ${ganttPhaseClassName}`}
                      onMouseEnter={() => setHoveredRow({ type: "phase", id: phase.id })}
                      onMouseLeave={() => setHoveredRow(null)}
                      data-hovered={hoveredRow?.type === "phase" && hoveredRow.id === phase.id}
                    >
                      <div className={ganttChartClassName}>
                        {phase.startDate && phase.deadlineDate ? (
                          renderBar("phase", phase)
                        ) : (
                          <div
                            className={ganttChartClassName}
                            style={{ cursor: "crosshair" }}
                            onMouseDown={(e) => handleChartAreaMouseDown(e, "phase", phase.id)}
                          />
                        )}
                      </div>
                    </div>

                    {phase.tasks?.map((task) => (
                      <div
                        key={task.id}
                        className={`${ganttRowClassName} ${ganttTaskClassName} ${
                          collapsedPhases[phase.id] ? collapsedClassName : ""
                        }`}
                        onMouseEnter={() => setHoveredRow({ type: "task", id: task.id })}
                        onMouseLeave={() => setHoveredRow(null)}
                        data-hovered={hoveredRow?.type === "task" && hoveredRow.id === task.id}
                      >
                        <div className={ganttChartClassName}>
                          {task.startDate && task.deadlineDate ? (
                            renderBar("task", task, phase.id)
                          ) : (
                            <div
                              className={ganttChartClassName}
                              style={{ cursor: "crosshair" }}
                              onMouseDown={(e) =>
                                handleChartAreaMouseDown(e, "task", task.id, phase.id)
                              }
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
      {timelineEdit && (
        <TimelineModal
          isOpen={true}
          onClose={() => setTimelineEdit(null)}
          mode={timelineEdit.type}
          title={timelineEdit.title}
          startDate={timelineEdit.startDate}
          deadlineDate={timelineEdit.deadlineDate}
          onSave={handleTimelineChange}
        />
      )}
    </div>
  );
};
